/* eslint-disable react-hooks/exhaustive-deps */
import React, { FunctionComponent, useEffect, useState, useCallback, useRef } from "react";
import styled from "styled-components";
import Text from "../../../../components/Text";
import {
  usePostsHandlers,
  IWorkspacePost,
  TFeedSyncProgress,
} from "../../../../services/actions/posts";
import PostCard from "./PostCard";
import LoadingSpinner from "../../../../components/LoadingSpinner";
import Row from "../../../../components/Row";
import Column from "../../../../components/Column";
import Button from "../../../../components/Button";
import Select from "../../../../components/select/Select";
import FormControl from "../../../../components/FormControl";
import DropdownMenu from "../../../../components/dropdown/DropdownMenu";
import { useFeedsHandlers } from "../../../../services/actions/feeds/useFeedsHandlers";
import { useFeedsStore } from "../../../../stores/feeds/useFeedsStore";
import { useAppHandlers } from "../../../../services/actions/app/useAppHandlers";
import { AppRoutes } from "../../../../interfaces/store/appStore";
import { useNavigate } from "react-router-dom";
import { IFeed } from "../../../../interfaces/models/feed";
import { styles } from "../../../../styles/themes/style";
import ManageFeeds from "./ManageFeeds";
import SvgFragment from "../../../../fragments/SvgFragment";
import ActionText from "../../../../components/ActionText";
import { getID } from "../../../../utils";
import Input from "../../../../components/Input";
import DropdownItem from "../../../../components/dropdown/DropdownItem";
import { useAuthStore } from "../../../../stores/auth/useAuthStore";
import { useAppStore } from "../../../../stores/application/useAppStore";
import {
  canSyncFeed,
  canSyncGlobalTracked,
  formatCooldownRemaining,
  getFeedSyncCooldownRemainingMs,
  getGlobalTrackedSyncCooldownRemainingMs,
  setFeedLastSyncedAt,
  setGlobalTrackedLastSyncedAt,
} from "../../utils/feedSyncCooldown";

interface IPostsFeedProps { 
  containerRef?: React.RefObject<HTMLDivElement>;
}

// Create Feed Dropdown Component - defined outside to prevent re-renders
interface ICreateFeedDropdownProps {
  setCreateFeedModal: (selectedConnectionsIds?: string[], disableExistingFeed?: boolean, onClose?: () => void, urlMode?: boolean) => void;
}

const CreateFeedDropdown: FunctionComponent<ICreateFeedDropdownProps> = ({ setCreateFeedModal }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  return (
    <DropdownMenu
      tooltipMessage="Create new feed"
      title={
        <CreateFeedButtonContainer>
          <SvgFragment type="plus" />
          <Text>Create new feed</Text>
          <SvgFragment type="arrowDown" />
        </CreateFeedButtonContainer>
      }
      show={isDropdownOpen}
      onToggle={setIsDropdownOpen}
      $hideToggle
    >
      <DropdownItem
        onClickHandler={() => {
          setIsDropdownOpen(false);
          setCreateFeedModal([], false, undefined, true); // urlMode = true
        }}
      >
        <Row alignItems gap="8px">
          <IconWrapper>
            <SvgFragment type="linkedin" />
          </IconWrapper>
          <Text>Add LinkedIn URLs</Text>
        </Row>
      </DropdownItem>
      <DropdownItem
        onClickHandler={() => {
          setIsDropdownOpen(false);
          setCreateFeedModal([], true); // Regular mode
        }}
      >
        <Row alignItems gap="8px">
          <IconWrapper>
            <SvgFragment type="profiles" />
          </IconWrapper>
          <Text>Add LeadDelta contacts</Text>
        </Row>
      </DropdownItem>
    </DropdownMenu>
  );
};

const PostsFeed: FunctionComponent<IPostsFeedProps> = ({ containerRef }) => {
  const { getWorkspacePostsHandler, syncFeedConnectionsHandler, syncTrackedConnectionsHandler } = usePostsHandlers();
  const { getFeedsHandler, getFeedConnectionsHandler } = useFeedsHandlers();
  const { setCreateFeedModal, showErrorToast, showSuccessToast, showInfoToast, showWarningToast } = useAppHandlers();
  const { store: { feeds, numConnections = -1, maxNumConnections = 0 } } = useFeedsStore();
  const { store: { isLoggedToLinkedin } } = useAuthStore();
  const { store: { extensionInstalled } } = useAppStore();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<IWorkspacePost[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [filterLoading, setFilterLoading] = useState<boolean>(false);
  const [postsLoaded, setPostsLoaded] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [selectedFeed, setSelectedFeed] = useState<any>(null);
  const [feedsWhenManageOpened, setFeedsWhenManageOpened] = useState<IFeed[]>([]);
  const [sortBy, setSortBy] = useState<any>({
    value: "newest",
    label: <Text $label>{"Newest"}</Text>
  });
  const [maxPostsPerUser, setMaxPostsPerUser] = useState<any>({ value: 3, label: <Text $label>{"3 post/user"}</Text> });
  const [isTimeDropdownOpen, setIsTimeDropdownOpen] = useState<boolean>(false);
  const [customDays, setCustomDays] = useState<number>(14);
  const [tempCustomDays, setTempCustomDays] = useState<number>(14);
  const [showManageFeeds, setShowManageFeeds] = useState<boolean>(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState<boolean>(false);
  const [commentsThisWeekCount, setCommentsThisWeekCount] = useState<number>(0);
  const [commentsWeeklyGoal, setCommentsWeeklyGoal] = useState<number>(100);
  const [isProgressExpanded, setIsProgressExpanded] = useState<boolean>(true);
  const [isWeeklyProgressLoading, setIsWeeklyProgressLoading] = useState<boolean>(true);
  const [loadingPostsAfterAdd, setLoadingPostsAfterAdd] = useState<boolean>(false);
  const [feedSyncProgress, setFeedSyncProgress] = useState<TFeedSyncProgress | null>(null);
  const [isFeedSyncing, setIsFeedSyncing] = useState(false);
  const [isGlobalPostsSync, setIsGlobalPostsSync] = useState(false);
  const [cooldownRemainingMs, setCooldownRemainingMs] = useState(0);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const feedSyncCancelRef = useRef(false);

  // Search state - simplified for post content search only
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const [searchValues, setSearchValues] = useState<Array<{
    uid: string;
    searchValue: string;
    isOutterValueSearchOr: string;
  }>>([]);

  // Check if feed limit is reached
  const isLimitReached = numConnections >= maxNumConnections;

  // localStorage keys
  const STORAGE_KEYS = {
    SELECTED_FEED: 'postsSection_selectedFeed',
    SORT_BY: 'postsSection_sortBy',
    MAX_POSTS_PER_USER: 'postsSection_maxPostsPerUser',
    CUSTOM_DAYS: 'postsSection_customDays',
  };

  // Helper functions for localStorage
  const saveToStorage = (key: string, value: any) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  const loadFromStorage = (key: string, defaultValue: any) => {
    try {
      const saved = localStorage.getItem(key);
      return saved ? JSON.parse(saved) : defaultValue;
    } catch (error) {
      console.error('Error loading from localStorage:', error);
      return defaultValue;
    }
  };
  
  // Refs to store current filter values for loadMore
  const currentFiltersRef = useRef({
    sortBy: { value: "newest", label: <Text $label>{"Newest"}</Text>} as any,
    selectedFeed: null as any,
    maxPostsPerUser: { value: 3, label: <Text $label>{"3 post/user"}</Text>} as any,
    customDays: 14,
    searchValues: [] as Array<{
      uid: string;
      searchValue: string;
      isOutterValueSearchOr: string;
    }>
  });

  // Select options
  const sortByOptions = [
    { value: "newest", label: "Newest" },
    { value: "oldest", label: "Oldest" },
    { value: "most_engagement", label: "Most engagement" },
    { value: "least_engagement", label: "Least engagement" },
  ];

  const maxPostsOptions = [
    { value: 1, label: "1 post/user" },
    { value: 2, label: "2 post/user" },
    { value: 3, label: "3 post/user" },
    { value: 5, label: "5 post/user" },
    { value: 10, label: "10 post/user" },
    { value: 20, label: "20 post/user" },
  ];

  const handlePost = useCallback((postId: string, handler: string, reacted: string | null, num_likes: number | null, num_comments: number | null) => {
    if (handler === 'skipped') {
      // Remove the post from the list when skipped
      setPosts(prevPosts => prevPosts.filter(post => (post.posts as any)?.post?.id !== postId));
      return;
    }

    // Increment comments count when a comment is added
    if (handler === 'num_comments') {
      setCommentsThisWeekCount(prev => prev + 1);
    }

    setPosts(prevPosts => {
      return prevPosts.map(post => {
        if ((post.posts as any)?.post?.id === postId) {
          return {
            ...post,
            posts: {
              ...post.posts,
              post: {
                ...(post.posts as any)?.post,
                blob: {
                  ...(post.posts as any)?.post?.blob,
                  ...(handler === 'reacted' && { reacted: reacted, num_likes: num_likes }),
                  ...(handler === 'num_comments' && { num_comments: num_comments })
                }
              }
            }
          }
        }
        return post
      })
    })
  }, [])

  const loadPosts = useCallback(
    async (pageNumber: number = 1, append: boolean = false, useRefFilters: boolean = false) => {
        try {
            if (pageNumber === 1) {
              if (useRefFilters) {
                setFilterLoading(true);
              } else {
                setLoading(true);
              }
              // Reset postsLoaded when loading new posts (not appending)
              setPostsLoaded(false);
            } else {
          setLoadingMore(true);
        }

        // Prepare query parameters
        const queryParams: any = {
          page: pageNumber,
          limit: 10,
        };

        // Use ref filters for loadMore, state filters for initial load
        const filters = useRefFilters ? currentFiltersRef.current : {
          sortBy,
          selectedFeed,
          maxPostsPerUser: currentFiltersRef.current.maxPostsPerUser, // Always use ref for maxPostsPerUser
          customDays,
          searchValues: searchValues.length > 0 ? searchValues : currentFiltersRef.current.searchValues
        };

        // Add sort parameter
        if (filters.sortBy?.value) {
          queryParams.sort = filters.sortBy.value;
        }

        // Add feed filter
        if (filters.selectedFeed?.value && filters.selectedFeed.value !== 'all') {
          queryParams.feedId = filters.selectedFeed.value;
        }

        // Add max posts per user filter
        if (filters.maxPostsPerUser?.value) {
          queryParams.maxPostsPerUser = filters.maxPostsPerUser.value;
        }

        // Add time range filter
        if (filters.customDays) {
          queryParams.timeRange = filters.customDays;
        }

        // Add search values filter
        if (filters.searchValues && filters.searchValues.length > 0 && filters.searchValues[0]?.searchValue) {
          queryParams.searchValues = encodeURIComponent(JSON.stringify(filters.searchValues));
        }

        const result = await getWorkspacePostsHandler(queryParams);
            
            if (result) {
                if (append) {
            setPosts((prev) => {
              const seenIds = new Set(
                prev
                  .map((post) => (post.posts as any)?.post?.id)
                  .filter(Boolean)
              );
              const uniqueNewPosts = result.posts.filter((post: IWorkspacePost) => {
                const postId = (post.posts as any)?.post?.id;
                if (!postId || seenIds.has(postId)) {
                  return false;
                }
                seenIds.add(postId);
                return true;
              });
              return [...prev, ...uniqueNewPosts];
            });
                } else {
            setPosts(result.posts);
                }
          setHasMore(pageNumber < result.pagination.totalPages);
          setPostsLoaded(true);
            }
        } catch (error) {
        console.error("Error loading posts:", error);
        } finally {
        setLoading(false);
        setFilterLoading(false);
        setLoadingMore(false);
        }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [sortBy, selectedFeed, customDays, searchValues]
  );

  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      setPage((prev) => {
        const nextPage = prev + 1;
        return nextPage;
      });
      // Call loadPosts after state update
      const nextPage = page + 1;
      await loadPosts(nextPage, true, true); // useRefFilters = true
    } else {
      console.log("LoadMore blocked:", { loadingMore, hasMore });
    }
  }, [loadPosts, loadingMore, hasMore, page]);

    const handleScroll = useCallback(() => {
    if (!containerRef?.current) return;
        
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const isNearBottom = scrollHeight - scrollTop <= clientHeight + 100;
        
        // Only load more if we're near bottom and not already loading
        if (!loadingMore && hasMore && isNearBottom) {
      loadMore();
        }
  }, [loadMore, loadingMore, hasMore, containerRef]);

  // State to track if initialization is complete
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoadingFromStorage, setIsLoadingFromStorage] = useState(true);

  // Initialize state from localStorage on component mount
  useEffect(() => {
    const initializeFromStorage = () => {
      const showManage = localStorage.getItem('postsSection_showManageFeeds') === 'true';

      // Set showManageFeeds if it was set in localStorage
      if (showManage) {
        setShowManageFeeds(true);
        // Save current feeds state since we're going to ManageFeeds from localStorage
        setFeedsWhenManageOpened([...feeds]);
        setIsInitialized(true);
        setIsLoadingFromStorage(false);
        return; // Don't load posts if showing manage feeds
      }

      // Load saved filter values from localStorage
      const savedSelectedFeed = loadFromStorage(STORAGE_KEYS.SELECTED_FEED, null);
      const savedSortBy = loadFromStorage(STORAGE_KEYS.SORT_BY, { value: "newest", label: "Newest" });
      const savedMaxPostsPerUser = loadFromStorage(STORAGE_KEYS.MAX_POSTS_PER_USER, { value: 3, label: "3 post/user" });
      const savedCustomDays = loadFromStorage(STORAGE_KEYS.CUSTOM_DAYS, 14);

      // Set state with saved values
      if (savedSelectedFeed) {
        // Always set the saved feed first, we'll validate it later
        const feedOption = {
          label: <Text $label>{savedSelectedFeed.label}</Text>,
          value: savedSelectedFeed.value,
        };
        setSelectedFeed(feedOption);
        currentFiltersRef.current.selectedFeed = feedOption;
      }

      if (savedSortBy) {
        const sortOption = {
          label: <Text $label>{savedSortBy.label}</Text>,
          value: savedSortBy.value,
        };
        setSortBy(sortOption);
        currentFiltersRef.current.sortBy = sortOption;
      }

      if (savedMaxPostsPerUser) {
        const maxPostsOption = {
          label: <Text $label>{savedMaxPostsPerUser.label}</Text>,
          value: savedMaxPostsPerUser.value,
        };
        setMaxPostsPerUser(maxPostsOption);
        currentFiltersRef.current.maxPostsPerUser = maxPostsOption;
      }

      if (savedCustomDays) {
        setCustomDays(savedCustomDays);
        setTempCustomDays(savedCustomDays);
        currentFiltersRef.current.customDays = savedCustomDays;
      }

      setIsInitialized(true);
      setIsLoadingFromStorage(false);
    };

    getFeedsHandler().then((result) => {
      if (result && result.commentsThisWeekCount !== undefined) {
        setCommentsThisWeekCount(result.commentsThisWeekCount);
      }
      if (result && result.commentsWeeklyGoal !== undefined) {
        setCommentsWeeklyGoal(result.commentsWeeklyGoal);
      }
      setIsWeeklyProgressLoading(false);
      initializeFromStorage();
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load posts after initialization is complete
  useEffect(() => {
    if (isInitialized && !showManageFeeds) {
      loadPosts(1);
    }
  }, [isInitialized, showManageFeeds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Validate saved feed when feeds are loaded
  useEffect(() => {
    if (feeds.length > 0 && selectedFeed && selectedFeed.value !== 'all') {
      const feedExists = feeds.some((feed: IFeed) => feed._id === selectedFeed.value);
      if (!feedExists) {
        saveToStorage(STORAGE_KEYS.SELECTED_FEED, null);
        setSelectedFeed(null);
        currentFiltersRef.current.selectedFeed = null;
      }
    }
  }, [feeds, selectedFeed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load posts when showManageFeeds changes from true to false
  useEffect(() => {
    if (!showManageFeeds && feedsWhenManageOpened.length > 0) {
      // Always refetch posts when returning from ManageFeeds
      // This covers feed deletion, connection removal, and any other changes
      console.log('Loading posts after returning from ManageFeeds - ensuring fresh data');
      loadPosts(1);
      
      // Reset the comparison array
      setFeedsWhenManageOpened([]);
    }
  }, [showManageFeeds]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFeedChange = (selectedOption: any) => {
    const newFeed = {
      label: <Text $label>{selectedOption.label}</Text>,
      value: selectedOption.value,
    };
    setSelectedFeed(newFeed);
    currentFiltersRef.current.selectedFeed = newFeed;
    
    // Save to localStorage
    saveToStorage(STORAGE_KEYS.SELECTED_FEED, {
      label: selectedOption.label,
      value: selectedOption.value,
    });
    
    // Keep existing filter and sort values - only reset page
    setPage(1);
    loadPosts(1, false, true); // useRefFilters = true
  };

  const selectedFeedId =
    selectedFeed?.value && selectedFeed.value !== 'all' ? String(selectedFeed.value) : null;
  const isAllFeedsSelected = !selectedFeedId;

  // Refresh cooldown countdown (per-feed or global tracked sync)
  useEffect(() => {
    const tick = () => {
      setCooldownRemainingMs(
        selectedFeedId
          ? getFeedSyncCooldownRemainingMs(selectedFeedId)
          : getGlobalTrackedSyncCooldownRemainingMs(),
      );
    };
    tick();
    const id = window.setInterval(tick, 15_000);
    return () => window.clearInterval(id);
  }, [selectedFeedId, isFeedSyncing]);

  const handleSyncFeedNow = useCallback(async () => {
    if (isFeedSyncing) return;

    if (!extensionInstalled) {
      showErrorToast({ message: 'Install the LeadDelta extension to sync feed posts.' });
      return;
    }
    if (!isLoggedToLinkedin) {
      showErrorToast({ message: 'Log in to LinkedIn in the extension first.' });
      return;
    }

    if (isAllFeedsSelected) {
      if (!canSyncGlobalTracked()) {
        const remaining = formatCooldownRemaining(getGlobalTrackedSyncCooldownRemainingMs());
        showInfoToast({ message: `Posts were synced recently. Try again in ${remaining}.` });
        setCooldownRemainingMs(getGlobalTrackedSyncCooldownRemainingMs());
        return;
      }

      feedSyncCancelRef.current = false;
      setIsGlobalPostsSync(true);
      setIsFeedSyncing(true);
      setFeedSyncProgress({
        index: 0,
        total: 0,
        displayName: '',
        status: 'syncing',
        synced: 0,
        failed: 0,
        skipped: 0,
      });

      try {
        const syncResult = await syncTrackedConnectionsHandler({
          shouldCancel: () => feedSyncCancelRef.current,
          onProgress: (progress) => setFeedSyncProgress(progress),
        });

        if (syncResult.total === 0 && !syncResult.cancelled) {
          showInfoToast({ message: 'No tracked contacts to sync.' });
          return;
        }

        if (syncResult.cancelled) {
          showWarningToast({ message: 'Posts sync cancelled.' });
        } else {
          setGlobalTrackedLastSyncedAt();
          setCooldownRemainingMs(getGlobalTrackedSyncCooldownRemainingMs());
          const parts = [`${syncResult.synced} updated`];
          if (syncResult.failed > 0) parts.push(`${syncResult.failed} failed`);
          if (syncResult.skipped > 0) parts.push(`${syncResult.skipped} skipped`);
          showSuccessToast({ message: `Posts sync done · ${parts.join(' · ')}` });
          await loadPosts(1, false, true);
        }
      } catch (error) {
        console.error('Tracked posts sync failed:', error);
        showErrorToast({ message: 'Failed to sync posts.' });
      } finally {
        setIsFeedSyncing(false);
        setIsGlobalPostsSync(false);
        setFeedSyncProgress(null);
        feedSyncCancelRef.current = false;
      }
      return;
    }

    if (!canSyncFeed(selectedFeedId)) {
      const remaining = formatCooldownRemaining(getFeedSyncCooldownRemainingMs(selectedFeedId));
      showInfoToast({ message: `This feed was synced recently. Try again in ${remaining}.` });
      setCooldownRemainingMs(getFeedSyncCooldownRemainingMs(selectedFeedId));
      return;
    }

    feedSyncCancelRef.current = false;
    setIsGlobalPostsSync(false);
    setIsFeedSyncing(true);
    setFeedSyncProgress({
      index: 0,
      total: 0,
      displayName: '',
      status: 'syncing',
      synced: 0,
      failed: 0,
      skipped: 0,
    });

    try {
      const result = await getFeedConnectionsHandler(selectedFeedId);
      const connections = result?.connections || [];
      if (!connections.length) {
        showInfoToast({ message: 'This feed has no contacts to sync.' });
        setFeedSyncProgress(null);
        return;
      }

      const syncResult = await syncFeedConnectionsHandler(connections, {
        shouldCancel: () => feedSyncCancelRef.current,
        onProgress: (progress) => setFeedSyncProgress(progress),
      });

      if (syncResult.cancelled) {
        showWarningToast({ message: 'Feed sync cancelled.' });
      } else {
        setFeedLastSyncedAt(selectedFeedId);
        setCooldownRemainingMs(getFeedSyncCooldownRemainingMs(selectedFeedId));
        const parts = [`${syncResult.synced} updated`];
        if (syncResult.failed > 0) parts.push(`${syncResult.failed} failed`);
        if (syncResult.skipped > 0) parts.push(`${syncResult.skipped} skipped`);
        showSuccessToast({ message: `Feed sync done · ${parts.join(' · ')}` });
        await loadPosts(1, false, true);
      }
    } catch (error) {
      console.error('Feed sync failed:', error);
      showErrorToast({ message: 'Failed to sync feed posts.' });
    } finally {
      setIsFeedSyncing(false);
      setIsGlobalPostsSync(false);
      setFeedSyncProgress(null);
      feedSyncCancelRef.current = false;
    }
  }, [
    selectedFeedId,
    isAllFeedsSelected,
    isFeedSyncing,
    extensionInstalled,
    isLoggedToLinkedin,
    getFeedConnectionsHandler,
    syncFeedConnectionsHandler,
    syncTrackedConnectionsHandler,
    showErrorToast,
    showSuccessToast,
    showInfoToast,
    showWarningToast,
    loadPosts,
  ]);

  const handleSortChange = (selectedOption: any) => {
    const newSort = {
      label: <Text $label>{selectedOption.label}</Text>,
      value: selectedOption.value,
    };
    setSortBy(newSort);
    currentFiltersRef.current.sortBy = newSort;
    
    // Save to localStorage
    saveToStorage(STORAGE_KEYS.SORT_BY, {
      label: selectedOption.label,
      value: selectedOption.value,
    });
    
    // Reset to first page when sort changes
    setPage(1);
    loadPosts(1, false, true); // useRefFilters = true
  };

  const handleMaxPostsChange = (selectedOption: any) => {
    const newMaxPosts = {
      label: <Text $label>{selectedOption.label}</Text>,
      value: selectedOption.value,
    };
    setMaxPostsPerUser(newMaxPosts);
    currentFiltersRef.current.maxPostsPerUser = newMaxPosts;
    
    // Save to localStorage
    saveToStorage(STORAGE_KEYS.MAX_POSTS_PER_USER, {
      label: selectedOption.label,
      value: selectedOption.value,
    });
    
    // Reset to first page when max posts changes
    setPage(1);
    loadPosts(1, false, true); // useRefFilters = true
  };

  const handleCustomDaysChange = (days: number) => {
    setTempCustomDays(days);

    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout for API call - only after user stops moving slider
    debounceTimeoutRef.current = setTimeout(() => {
      // Update state and ref BEFORE calling loadPosts
      setCustomDays(days);
      currentFiltersRef.current.customDays = days;
      
      // Save to localStorage
      saveToStorage(STORAGE_KEYS.CUSTOM_DAYS, days);
      
      // Reset to first page when custom days change
      setPage(1);
      // Now call loadPosts with the updated ref values
      loadPosts(1, false, true); // useRefFilters = true
    }, 500); // 500ms delay
  };

  // Search handlers - simplified for post content search
  const inputChangedHandler = (value: string) => {
    setSearchInputValue(value);
  };

  const keyDownChangeHandler = async (event: KeyboardEvent) => {
    if (event.key === 'Enter' && searchInputValue.trim()) {
      event.preventDefault();
      const newSearchValue = {
        uid: getID(),
        searchValue: searchInputValue.trim(),
        isOutterValueSearchOr: 'no', // Default to AND
      };
      const newSearchValues = [...searchValues, newSearchValue];
      setSearchValues(newSearchValues);
      currentFiltersRef.current.searchValues = newSearchValues;
      
      // Reset input
      setSearchInputValue('');
      
      // Reset to first page and reload posts
      setPage(1);
      loadPosts(1, false, true);
    }
  };

  const handleRemoveSearchTag = (uid: string) => {
    const newSearchValues = searchValues.filter(val => val.uid !== uid);
    setSearchValues(newSearchValues);
    currentFiltersRef.current.searchValues = newSearchValues;
    
    // Reset to first page and reload posts
    setPage(1);
    loadPosts(1, false, true);
  };

  const handleToggleSearchOperator = (uid: string, operator: 'yes' | 'no') => {
    const newSearchValues = searchValues.map(val =>
      val.uid === uid ? { ...val, isOutterValueSearchOr: operator } : val
    );
    setSearchValues(newSearchValues);
    currentFiltersRef.current.searchValues = newSearchValues;
    
    // Reset to first page and reload posts
    setPage(1);
    loadPosts(1, false, true);
  };

  // const handleClearAllSearch = () => {
  //   setSearchValues([]);
  //   currentFiltersRef.current.searchValues = [];
    
  //   // Reset to first page and reload posts
  //   setPage(1);
  //   loadPosts(1, false, true);
  // };

  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll);
        
        return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll, containerRef]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Listen for refresh event from CreateFeedModal
  useEffect(() => {
    const handleRefreshPosts = async () => {
      // Refresh posts when new connections are added to feeds via URLs
      // Stop loading after getWorkspacePostsHandler completes
      await loadPosts(1, false, true);
      // After posts are loaded, stop the loading indicator
      setLoadingPostsAfterAdd(false);
    };

    window.addEventListener('refreshPostsFeed', handleRefreshPosts);
    
    return () => {
      window.removeEventListener('refreshPostsFeed', handleRefreshPosts);
    };
  }, [loadPosts]);

  // Listen for loading state events from CreateFeedModal
  useEffect(() => {
    const handleStartLoading = () => {
      setLoadingPostsAfterAdd(true);
    };

    const handleStopLoading = () => {
      setLoadingPostsAfterAdd(false);
    };

    window.addEventListener('startLoadingPostsAfterAdd', handleStartLoading);
    window.addEventListener('stopLoadingPostsAfterAdd', handleStopLoading);
    
    return () => {
      window.removeEventListener('startLoadingPostsAfterAdd', handleStartLoading);
      window.removeEventListener('stopLoadingPostsAfterAdd', handleStopLoading);
    };
  }, []);

  // Function to refresh filters from localStorage
  const refreshFiltersFromStorage = useCallback(() => {
    // Load saved filter values from localStorage
    const savedSelectedFeed = loadFromStorage(STORAGE_KEYS.SELECTED_FEED, null);
    const savedSortBy = loadFromStorage(STORAGE_KEYS.SORT_BY, { value: "newest", label: "Newest" });
    const savedMaxPostsPerUser = loadFromStorage(STORAGE_KEYS.MAX_POSTS_PER_USER, { value: 3, label: "3 post/user" });
    const savedCustomDays = loadFromStorage(STORAGE_KEYS.CUSTOM_DAYS, 14);

    // Set state with saved values
    if (savedSelectedFeed) {
      const feedOption = {
        label: <Text $label>{savedSelectedFeed.label}</Text>,
        value: savedSelectedFeed.value,
      };
      setSelectedFeed(feedOption);
      currentFiltersRef.current.selectedFeed = feedOption;
    }

    if (savedSortBy) {
      const sortOption = {
        label: <Text $label>{savedSortBy.label}</Text>,
        value: savedSortBy.value,
      };
      setSortBy(sortOption);
      currentFiltersRef.current.sortBy = sortOption;
    }

    if (savedMaxPostsPerUser) {
      const maxPostsOption = {
        label: <Text $label>{savedMaxPostsPerUser.label}</Text>,
        value: savedMaxPostsPerUser.value,
      };
      setMaxPostsPerUser(maxPostsOption);
      currentFiltersRef.current.maxPostsPerUser = maxPostsOption;
    }

    if (savedCustomDays) {
      setCustomDays(savedCustomDays);
      setTempCustomDays(savedCustomDays);
      currentFiltersRef.current.customDays = savedCustomDays;
    }
  }, []);

  if (showManageFeeds) {
    return <ManageFeeds onBack={() => {
      setShowManageFeeds(false);
      // Remove only the showManageFeeds flag from localStorage
      localStorage.removeItem('postsSection_showManageFeeds');
      localStorage.removeItem('postsSection_showConnections');
      localStorage.removeItem('postsSection_selectedFeedId');
      
      // Refresh filters from localStorage
      refreshFiltersFromStorage();
      
      // Reload posts to ensure fresh data after any feed changes
      loadPosts(1);
    }} />;
  }

  const selectedTimeFilterButton = (
    <TimeFilterContainer>
      <Text>Within {tempCustomDays} days</Text>
    </TimeFilterContainer>
  );

  // Show "No feeds created yet" only if there are no feeds at all
  if (feeds.length === 0 && !loading) {
    return (
      <Container>
        <PostsContainer>
          <Column gap="12px">
            <Row gap="12px" spaceBetween>
              <Column gap="12px">
                <Text $heading3>Custom feeds</Text>
                <Text $paragraph2>Create feeds based on people you want to engage with. Comment and react when they post to LinkedIn. We suggest creating separate feeds for categories such as target prospects, existing customers, competitors, and influencers.</Text>
              </Column>
              <Column>
                  <CreateFeedDropdown setCreateFeedModal={setCreateFeedModal} />
              </Column>
            </Row>
            <Column gap="10px" style={{ alignItems: 'center', width: '100%', marginTop: '60px', marginBottom: '100px' }}>
                <SvgFragment type="feedEmptyState" />
                <Text $heading3>No feeds created yet</Text>
                <Text $paragraph2>Create your first feed, add contacts whose posts you want to track and start engaging</Text>
                <VideoContainer>
                  <VideoIframe 
                    src="https://www.loom.com/embed/378fc6b2ce784373a588586d624a5b3c?sid=520767bb-84a2-49bf-8a31-71da8ce1994d" 
                    allowFullScreen 
                  />
                </VideoContainer>
            </Column>
          </Column>
        </PostsContainer>
      </Container>
    );
  }

    return (
        <Container>
            <PostsContainer>
        <Column gap="12px">
          <Row gap="12px" spaceBetween>
            <Column gap="12px">
              <Text $heading3>Custom feeds</Text>
              <Text $paragraph4>Create and engage with chosen feeds</Text>
            </Column>
            <Row gap="8px">
              <Button 
                $type="light-blue"
                $color={styles.colors.black600}
                $width="106px"
                onClickHandler={() => {
                  setShowManageFeeds(true);
                  // Save current feeds state to compare later
                  setFeedsWhenManageOpened([...feeds]);
                  // Save showManageFeeds to localStorage
                  localStorage.setItem('postsSection_showManageFeeds', 'true');
                }}
              >
                Manage feeds
              </Button>
              <CreateFeedDropdown setCreateFeedModal={setCreateFeedModal} />
            </Row>
          </Row>
          {isLimitReached && (
            <NoteBox $warning={true}>
              <Row gap="8px" alignItems>
                <SvgFragment type="warning" />
                <StyledText>
                  Limit reached. Please <ActionText $regular textDecoration="underline" onClickHandler={() => navigate(AppRoutes.BILLING)}>{' upgrade  '}</ActionText> to get more tracked profiles or <ActionText textDecoration="underline" $regular onClickHandler={() => window.open('mailto:sales@leaddelta.com')}>contact sales</ActionText>.
                </StyledText>
              </Row>
            </NoteBox>
          )}
          <ExpandableDescription $expanded={isDescriptionExpanded}>
                <Row gap="8px" spaceBetween style={{ width: '100%' }} onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}>
                  <Row gap="8px">
                    <SvgFragment type="infoCircle" />
                    <Text $paragraph4 $lighter>Please note that feeds are not realtime</Text>
                  </Row>
                  <Row>
                    <Text className="cursor-pointer" $paragraph4 $lighter>{isDescriptionExpanded ? 'Show less' : 'Learn more'}</Text>
                  </Row>
                </Row>
                <ExpandedContent $expanded={isDescriptionExpanded}>
                  <Text $paragraph4 $lighter>
                    LeadDelta doesn't have a real-time LinkedIn feed. Due to limitations, we limit how frequently we pull data.
                  </Text>
                  <Text $paragraph4 $lighter>
                    While we often average 30-60 minutes to pull in posts, some come in faster, some less quickly. We use a proprietary prioritization algorithm to decide which profiles will get checked more often (usually, more 'popular' profiles).
                  </Text>
                  <Text $paragraph4 $lighter>
                    You'll often see posts in your feed that are less than 15 minutes old, but it's also not unheard of for us to take a few hours to pull in someone's post.
                  </Text>
                </ExpandedContent>
          </ExpandableDescription>
           {/* Search Input */}
           <Row style={{ width: '100%' }}>
               <FormControl $orientation="column">
                  <InputWithIcon>
                      <SearchIconWrapper>
                          <SvgFragment type="search" />
                      </SearchIconWrapper>
                      <Input 
                          placeholder="Search keywords"
                          onChangeHandler={(e: any) => inputChangedHandler(e)}
                          onKeyDownHandler={(e: KeyboardEvent) => keyDownChangeHandler(e)}
                          value={searchInputValue}
                          description="press enter"
                          style={{ height: '40px', paddingLeft: '36px' }}
                        />
                  </InputWithIcon>
               </FormControl>
             </Row>
            {/* Search Tags */}
            {searchValues.length > 0 && (
              <SearchTagsContainer>
                <Row gap="8px" style={{ flexWrap: 'wrap' }}>
                  {searchValues.map((searchObj, index) => {
                    const operator = index > 0 && searchObj?.isOutterValueSearchOr
                      ? searchObj?.isOutterValueSearchOr === 'yes' ? 'OR' : 'AND'
                      : undefined;

                    return (
                      <React.Fragment key={searchObj.uid}>
                            {operator && (
                              <SearchLabel style={{ padding: "0px 4px" }}>
                                <OperatorOption
                                  $isActive={operator === "AND"}
                                  onClick={() => handleToggleSearchOperator(searchObj.uid, "no")}
                                >
                                  And
                                </OperatorOption>
                                <OperatorSeparator />
                                <OperatorOption
                                  $isActive={operator === "OR"}
                                  onClick={() => handleToggleSearchOperator(searchObj.uid, "yes")}
                                >
                                  Or
                                </OperatorOption>
                              </SearchLabel>
                            )}
                            <SearchLabel style={{ padding: "2px 4px", display: "flex", alignItems: "center", gap: "6px" }}>
                              {searchObj.searchValue}
                              <SearchCloseButton
                                onClick={() => handleRemoveSearchTag(searchObj.uid)}
                                viewBox="0 0 12 12"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M1.96 11.825 6 7.785l4.04 4.04a.6.6 0 0 0 .846 0l.94-.939a.6.6 0 0 0 0-.847L7.785 6l4.039-4.04a.6.6 0 0 0 0-.846l-.939-.939a.6.6 0 0 0-.847 0L6 4.215 1.96.175a.6.6 0 0 0-.846 0l-.939.939a.6.6 0 0 0 0 .847L4.215 6 .175 10.04a.6.6 0 0 0 0 .847l.94.938a.6.6 0 0 0 .846 0z"
                                  fillRule="evenodd"
                                />
                              </SearchCloseButton>
                            </SearchLabel>
                          </React.Fragment>
                        );
                      })}
                    </Row>
              </SearchTagsContainer>
          )}
          <Column gap="12px">
            {isLoadingFromStorage ? (
              <FiltersLoadingContainer>
                <LoadingSpinner />
                <Text>Loading filters...</Text>
              </FiltersLoadingContainer>
            ) : (
              <>
                <Row gap="8px" alignItems style={{ maxWidth: '100%' }}>
                  <div style={{ flex: 1, maxWidth: '275px' }}>
                    <FormControl $orientation="column">
                      <Select
                        placeholder={<Text $label>{"All feeds"}</Text>}
                        isSearchable={false}
                        options={[
                          { value: "all", label: "All feeds" },
                          ...feeds.map((feed: IFeed) => ({
                            value: feed._id,
                            label: feed.name,
                          })),
                        ]}
                        value={selectedFeed}
                        onChange={handleFeedChange}
                      />
                    </FormControl>
                  </div>
                  <Button
                    $type="blue"
                    $customWidth
                    disabled={isFeedSyncing || cooldownRemainingMs > 0}
                    tooltipMessage={
                      isFeedSyncing
                        ? 'Sync in progress'
                        : cooldownRemainingMs > 0
                          ? `Available in ${formatCooldownRemaining(cooldownRemainingMs)}`
                          : isAllFeedsSelected
                            ? 'Sync all tracked contacts, one at a time'
                            : 'Sync this feed only, one contact at a time'
                    }
                    onClickHandler={() => { void handleSyncFeedNow(); }}
                  >
                    {isFeedSyncing
                      ? 'Syncing…'
                      : cooldownRemainingMs > 0
                        ? `Sync in ${formatCooldownRemaining(cooldownRemainingMs)}`
                        : isAllFeedsSelected
                          ? 'Sync posts'
                          : 'Sync this feed'}
                  </Button>
                </Row>
                {isFeedSyncing && feedSyncProgress && (
                  <FeedSyncBanner>
                    <Row gap="8px" alignItems spaceBetween style={{ width: '100%' }}>
                      <Row gap="8px" alignItems>
                        <LoadingSpinner />
                        <Column gap="2px">
                          <Text $paragraph3>
                            {isGlobalPostsSync ? 'Syncing posts' : 'Syncing feed'}
                            {' · '}
                            {feedSyncProgress.index}/{feedSyncProgress.total || '…'}
                            {feedSyncProgress.displayName
                              ? ` · ${feedSyncProgress.displayName}`
                              : ''}
                          </Text>
                          <Text $paragraph4 $lighter>
                            {feedSyncProgress.synced} updated
                            {feedSyncProgress.failed > 0 ? ` · ${feedSyncProgress.failed} failed` : ''}
                            {feedSyncProgress.skipped > 0 ? ` · ${feedSyncProgress.skipped} skipped` : ''}
                          </Text>
                        </Column>
                      </Row>
                      <ActionText
                        $regular
                        textDecoration="underline"
                        onClickHandler={() => { feedSyncCancelRef.current = true; }}
                      >
                        Stop
                      </ActionText>
                    </Row>
                  </FeedSyncBanner>
                )}
                <Row gap="8px" alignItems>
                  <div style={{ flex: 1 }}>
                    <FormControl $orientation="column">
                      <Select
                        isSearchable={false}
                        options={sortByOptions}
                        value={sortBy}
                        onChange={handleSortChange}
                      />
                    </FormControl>
                  </div>
                  <div style={{ flex: 1 }}>
                    <StyledDropdownMenu
                      selectStyle
                      tooltipMessage="Select time range"
                      title={selectedTimeFilterButton}
                      show={isTimeDropdownOpen}
                      onToggle={setIsTimeDropdownOpen}
                    >
                      <TimeSliderContainer>
                        <TimeSliderLabel>{tempCustomDays} day(s)</TimeSliderLabel>
                        <TimeSlider
                          type="range"
                          min="1"
                          max="100"
                          value={tempCustomDays}
                          title=""
                          onChange={(e) =>
                            handleCustomDaysChange(parseInt(e.target.value))
                          }
                        />
                      </TimeSliderContainer>
                    </StyledDropdownMenu>
                  </div>
                  <div style={{ flex: 1 }}>
                    <FormControl $orientation="column">
                      <Select
                        placeholder={<Text $label>{"Max 3 posts per user"}</Text>}
                        isSearchable={false}
                        options={maxPostsOptions}
                        value={maxPostsPerUser}
                        onChange={handleMaxPostsChange}
                      />
                    </FormControl>
                  </div>
                </Row>
              </>
            )}
          </Column>
          {isWeeklyProgressLoading ? (
            <ProgressLoadingContainer>
              <LoadingSpinner />
              <Text>Loading weekly progress...</Text>
            </ProgressLoadingContainer>
          ) : (
            <ProgressCard 
              commentsThisWeekCount={commentsThisWeekCount}
              commentsWeeklyGoal={commentsWeeklyGoal}
              isExpanded={isProgressExpanded}
              onToggle={() => setIsProgressExpanded(!isProgressExpanded)}
            />
          )}
          {(filterLoading || loading || !postsLoaded || loadingPostsAfterAdd) ? (
            <LoadingMoreContainer>
              <LoadingSpinner />
              <Text>Loading posts...</Text>
            </LoadingMoreContainer>
          ) : posts.length === 0 ? (
            <Column gap="10px" style={{ alignItems: 'center', width: '100%', marginTop: '40px', marginBottom: '40px' }}>
              <SvgFragment type="feedEmptyState" />
              <Text $heading3>No posts found</Text>
              <Text $paragraph2>Try adjusting your filters to see more posts</Text>
            </Column>
          ) : (
            <>
              {posts.map((post) => {
                const postId = (post.posts as any)?.post?.id || post._id;
                return (
                  <PostCard 
                    key={`${post._id}-${postId}`} 
                    post={post} 
                    onPostHandler={handlePost}
                    currentFeedId={selectedFeed?.value}
                    searchValues={searchValues}
                    onFeedUpdate={(updatedConnection) => {
                      // Optimistically update the post's feeds data
                      setPosts(prevPosts => 
                        prevPosts.map(prevPost => {
                          if (prevPost._id === updatedConnection._id) {
                            return {
                              ...prevPost,
                              feeds: updatedConnection.feeds
                            };
                          }
                          return prevPost;
                        })
                      );
                    }}
                    onCreateFeed={(connectionId) => {
                      // Open create feed modal with selected connection
                      setCreateFeedModal([connectionId], true);
                    }}
                  />
                );
              })}
              {loadingMore && (
                <LoadingMoreContainer>
                  <LoadingSpinner />
                  <Text>Loading more posts...</Text>
                </LoadingMoreContainer>
              )}
              {!hasMore && !loadingMore && posts.length > 0 && (
                <NoMorePostsContainer>
                  <Text $paragraph2>No more posts</Text>
                </NoMorePostsContainer>
              )}
            </>
          )}
        </Column>
            </PostsContainer>
        </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
`;

const PostsContainer = styled.div`
  width: 100%;
  max-width: 672px;
  margin: 0 auto;
  padding: 32px 24px;
  background: white;
  border-radius: 4px;
`;


const LoadingMoreContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px;
    gap: 12px;
`;

const FiltersLoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 40px;
    gap: 16px;
    min-height: 120px;
    justify-content: center;
`;

const ProgressLoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px;
    gap: 12px;
    margin: 16px 0;
    background: white;
    border-radius: 8px;
`;

const TimeSliderContainer = styled.div`
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-top: 8px;
`;

const TimeSliderLabel = styled(Text)`
  text-align: center;
  margin-bottom: 12px;
  font-weight: 500;
  color: #333;
`;

const TimeSlider = styled.input<{ value?: number }>`
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: #e0e0e0;
  outline: none;
  -webkit-appearance: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #5964ff;
    cursor: pointer;
    border: 2px solid white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #5964ff;
    cursor: pointer;
    border: 2px solid white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  &::-webkit-slider-track {
    background: linear-gradient(
      to right,
      #5964ff 0%,
      #5964ff ${(props) => ((props.value || 14) / 100) * 100}%,
      #e0e0e0 ${(props) => ((props.value || 14) / 100) * 100}%,
      #e0e0e0 100%
    );
  }
`;

const StyledDropdownMenu = styled(DropdownMenu)`
  border: ${({ border }) => (border ? `${border}` : "none")};
  .actions-dropdown + div {
    width: ${({ panelWidth }) => (panelWidth ? `${panelWidth}` : "100%")};
    max-height: 350px;
    overflow: hidden !important;
  }

  .plus-svg path {
    fill: ${styles.colors.primary600};
  }
`;

const TimeFilterContainer = styled.div`
  width: 100%;
  height: 30px;
  border-radius: 4px;
  opacity: 1;
  background: #ffffff;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;
`;

const ExpandableDescription = styled.div<{ $expanded: boolean }>`
  max-width: 624px;
  border-radius: 6px;
  border-left: 4px solid #5964FF;
  padding: 16px 16px 16px 20px;
  background: #F6F8FB;
  cursor: pointer;
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
  
  ${({ $expanded }) => $expanded ? `
    max-height: 500px;
  ` : `
    max-height: 52px;
  `}
 
  & .cursor-pointer {
    cursor: pointer;
    color: ${styles.colors.primary600};
    text-decoration: underline;
  }

  & svg path {
    fill: ${styles.colors.primary600};
  }
`;

const ExpandedContent = styled.div<{ $expanded: boolean }>`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  opacity: ${({ $expanded }) => $expanded ? '1' : '0'};
  transition: opacity 0.2s ease-in-out ${({ $expanded }) => $expanded ? '0.1s' : '0s'};
`;

const NoMorePostsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  margin-top: 20px;
`;

// ProgressCard component
interface IProgressCardProps {
  commentsThisWeekCount: number;
  commentsWeeklyGoal: number;
  isExpanded: boolean;
  onToggle: () => void;
}

const ProgressCard: FunctionComponent<IProgressCardProps> = ({ 
  commentsThisWeekCount, 
  commentsWeeklyGoal, 
  isExpanded, 
  onToggle 
}) => {
  const progressPercentage = Math.min((commentsThisWeekCount / commentsWeeklyGoal) * 100, 100);
  
  return (
    <ProgressCardContainer>
      <ProgressHeader onClick={onToggle}>
        <Row gap="8px" alignItems>
          <SvgFragment type="cup" />
          <Text $heading5>Your Progress</Text>
        </Row>
        <RotatedArrow $isExpanded={isExpanded}>
          <SvgFragment type="arrowDown" />
        </RotatedArrow>
      </ProgressHeader>
      
      {isExpanded && (
        <ProgressContent>
          <Row gap="12px" alignItems spaceBetween>
            <Text $paragraph2>Weekly Comments</Text>
            <Text $paragraph3>{commentsThisWeekCount}/{commentsWeeklyGoal}</Text>
          </Row>
          <ProgressBarContainer>
            <ProgressBarFill width={`${progressPercentage}%`} />
          </ProgressBarContainer>
          <RightAlignedText $paragraph3>{Math.round(progressPercentage)}% of goal</RightAlignedText>
        </ProgressContent>
      )}
    </ProgressCardContainer>
  );
};

const ProgressCardContainer = styled.div`
  background: white;
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  margin-bottom: 12px;
`;

const ProgressContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background: ${styles.colors.primary100};
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressBarFill = styled.div<{ width: string }>`
  height: 100%;
  width: ${props => props.width};
  background: ${styles.colors.primary300};
  border-radius: 4px;
  transition: width 0.3s ease;
`;

const RotatedArrow = styled.div<{ $isExpanded: boolean }>`
  display: inline-flex;
  transform: ${({ $isExpanded }) => $isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 0.3s ease;
`;

const RightAlignedText = styled(Text)`
  text-align: right;
  flex-direction: column;
`;

const NoteBox = styled.div<{ $warning?: boolean }>`
  padding: 12px 16px;
  background: ${({ $warning }) =>
    $warning ? styles.colors.yellow200 : "#f8f9fa"};
  border-left: 4px solid
    ${({ $warning }) =>
      $warning ? styles.colors.yellow600 : styles.colors.primary600};
  border-radius: 6px;
  padding: 16px 16px 16px 20px;
`;

const FeedSyncBanner = styled.div`
  width: 100%;
  padding: 12px 16px;
  background: ${styles.colors.primary100};
  border: 1px solid ${styles.colors.primary200};
  border-radius: 8px;
`;

const StyledText = styled(Text)`
  display: inline;
  text-align: left;

  & > * {
    display: inline-block;
  }
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 600px;
  margin-top: 20px;
  margin-left: auto;
  margin-right: auto;
  padding-left: 16px;
  padding-right: 16px;
  box-sizing: border-box;
  height: 300px;
`;

const VideoIframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
`;

const SearchTagsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const SearchLabel = styled.div`
  font-family: NunitoSans;
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
  background-color: ${styles.colors.primary100};
  border: 1px solid;
  border-color: ${styles.colors.primary600};
  color: ${styles.colors.primary600};
  border-radius: 4px;
  display: flex;
  align-items: center;
`;

const SearchCloseButton = styled.svg`
  width: 10px;
  height: 10px;
  background: none;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  path {
    fill: ${styles.colors.primary600};
  }
  &:hover path {
    fill: ${styles.colors.primary500};
  }
`;

interface IOperatorOptionProps {
  $isActive?: boolean;
}

const OperatorOption = styled.div<IOperatorOptionProps>`
  padding: 2px 4px;
  font-weight: ${({ $isActive }) => ($isActive ? "700" : "400")};
  color: ${styles.colors.primary600};
  line-height: 20px;
  cursor: pointer;
`;

const OperatorSeparator = styled.div`
  width: 1px;
  height: 100%;
  background-color: ${styles.colors.primary600};
`;

const InputWithIcon = styled.div`
  position: relative;
  width: 100%;
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  z-index: 1;
  pointer-events: none;
`;

const CreateFeedButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 166px;
  height: 34px;
  padding: 5px 8px 5px 8px;
  color: ${styles.colors.white};
  background-color: ${styles.colors.primary600};
  border: 1px solid ${styles.colors.primary600};
  border-radius: 4px;
  font-family: NunitoSans;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${styles.colors.primary400};
    border-color: ${styles.colors.primary400};
    
    svg path {
      fill: ${styles.colors.white} !important;
    }
  }

  svg {
    path {
      fill: ${styles.colors.white};
    }
  }

  span {
    color: ${styles.colors.white};
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

export default PostsFeed;
